import { useEffect, useState } from 'react';
import { X, Share, Plus, Smartphone, Download } from 'lucide-react';

const DISMISS_KEY = 'fencepost_install_banner_dismissed_v1';

/**
 * Platform-aware install banner.
 *   iOS Safari  → step-by-step Add-to-Home-Screen guide (Apple provides no
 *                 programmatic install prompt for PWAs on iOS).
 *   Android/Chromium → native beforeinstallprompt trigger, one-tap install.
 *   Standalone (already installed) → hidden.
 *   Desktop → hidden (keeps the landing hero clean; desktop users convert via
 *             the "Start Free Trial" CTA instead).
 *   Dismissed → hidden via localStorage until user reinstalls the app.
 */
export default function InstallAppBanner() {
  const [platform, setPlatform] = useState(null); // 'ios' | 'android' | null
  const [dismissed, setDismissed] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosDetails, setShowIosDetails] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (localStorage.getItem(DISMISS_KEY)) {
      setDismissed(true);
      return;
    }

    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) return; // already installed

    const ua = window.navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /android/i.test(ua);

    if (isIOS) {
      setPlatform('ios');
    } else if (isAndroid) {
      setPlatform('android');
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const triggerAndroidInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === 'accepted') dismiss();
  };

  if (dismissed || !platform) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 px-3 pb-3 pointer-events-none"
      data-testid="install-app-banner"
    >
      <div className="max-w-md mx-auto pointer-events-auto">
        <div className="bg-primary text-primary-foreground rounded-xl shadow-2xl overflow-hidden border border-primary/30">
          {/* Header row */}
          <div className="flex items-center gap-3 p-3">
            <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">Install Fencepost</div>
              <div className="text-xs opacity-90 truncate">
                {platform === 'ios'
                  ? 'Add to your Home Screen for the best experience'
                  : 'One tap to install as an app'}
              </div>
            </div>

            {platform === 'android' && deferredPrompt && (
              <button
                onClick={triggerAndroidInstall}
                data-testid="install-android-btn"
                className="text-xs font-semibold bg-white text-primary px-3 py-1.5 rounded-full whitespace-nowrap hover:bg-white/90 transition-colors"
              >
                <Download className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                Install
              </button>
            )}

            {platform === 'ios' && (
              <button
                onClick={() => setShowIosDetails((s) => !s)}
                data-testid="install-ios-toggle"
                className="text-xs font-semibold bg-white text-primary px-3 py-1.5 rounded-full whitespace-nowrap hover:bg-white/90 transition-colors"
              >
                {showIosDetails ? 'Hide' : 'How to'}
              </button>
            )}

            <button
              onClick={dismiss}
              aria-label="Dismiss install banner"
              data-testid="install-banner-dismiss"
              className="p-1 rounded-md hover:bg-white/10 transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* iOS instructions (collapsible) */}
          {platform === 'ios' && showIosDetails && (
            <div className="bg-white/10 px-3 py-3 space-y-2 text-sm">
              <IosStep num={1}>
                Tap the <Share className="w-4 h-4 inline -mt-0.5" /> <strong>Share</strong>{' '}
                icon at the bottom of Safari
              </IosStep>
              <IosStep num={2}>
                Scroll and tap <Plus className="w-4 h-4 inline -mt-0.5" />{' '}
                <strong>Add to Home Screen</strong>
              </IosStep>
              <IosStep num={3}>
                Tap <strong>Add</strong> — Fencepost will appear on your Home Screen
                like a normal app
              </IosStep>
              <p className="text-xs opacity-80 pt-1">
                Note: On iPhone this must be done in <strong>Safari</strong>. Chrome
                and Firefox on iOS do not support home-screen installs.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function IosStep({ num, children }) {
  return (
    <div className="flex gap-2">
      <span className="w-5 h-5 rounded-full bg-white/20 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
        {num}
      </span>
      <span className="text-primary-foreground/95">{children}</span>
    </div>
  );
}
